import { Epic, ofType } from "redux-observable";
import {
  catchError,
  debounceTime,
  map,
  mergeMap,
  switchMap,
} from "rxjs/operators";
import { CMSAsset } from "../../../../models/CMS/cmsAsset";
import { CMSEPGEvent } from "../../../../models/CMS/cmsEPGEvent";
import { CMSTag } from "../../../../models/CMS/cmsTag";
import { CMSGenre } from "../../../../models/CMS/cmsGenre";
import { CMSCollection } from "../../../../models/CMS/cmsCollection";
import { cmsService } from "../../../../http/cms.service";
import { SetError } from "../../../../redux/error.redux";
import { CMSMedia } from "../../../../models/CMS/cmsMedia";
import { CustomerPortalState } from "../index";
import { from } from "rxjs";
import { fromJson } from "../../../../models/modelHelpers";
import { createSelector } from "reselect";
import {
  SetNotificationError,
  SetNotificationSuccess,
} from "./notificationReducer";
import { CMSPagination } from "@nendaTypes/cms/CmsPaginationType";
import { CmsTag } from "@nendaTypes/cms/CmsTagType";
import { t } from "i18next";

export interface Provider {
  providerId: string;
  externalId: string;
  name: string;
}

export interface CMSState {
  isLoading: boolean;
  assets: CMSAsset[];
  pagination: CMSPagination;
  asset?: CMSAsset;
  channels: CMSAsset[];
  channel?: CMSAsset;
  EPG?: CMSEPGEvent[];
  tags: CMSTag[];
  tag?: CMSTag;
  genres: CMSGenre[];
  collections: CMSCollection[];
  collection?: CMSCollection;
  uploadedImages: { url: string; blobPath: string; index: number }[];
  providers: Provider[];
}

export const initialState: CMSState = {
  isLoading: false,
  assets: [],
  pagination: { total: 0, page: 1, pageSize: 25, next: null, previous: null },
  asset: undefined,
  channels: [],
  channel: undefined,
  EPG: [],
  tags: [],
  tag: undefined,
  genres: [],
  collections: [],
  collection: undefined,
  uploadedImages: [],
  providers: [],
};

export enum CMS_ACTION {
  //assets
  GET_ASSETS = "GET_ASSETS",
  SET_ASSETS = "SET_ASSETS",
  GET_ASSET = "GET_ASSET",
  SET_ASSET = "SET_ASSET",
  CLEAR_ASSET = "CLEAR_ASSET",
  CREATE_ASSET = "CREATE_ASSET",
  UPDATE_ASSET = "UPDATE_ASSET",
  DELETE_ASSET = "DELETE_ASSET",

  //providers
  GET_PROVIDERS = "GET_PROVIDERS",
  SET_PROVIDERS = "SET_PROVIDERS",

  // media
  CREATE_MEDIA = "CREATE_MEDIA",
  UPDATE_MEDIA = "UPDATE_MEDIA",
  DELETE_MEDIA = "DELETE_MEDIA",

  // image upload
  UPLOAD_IMAGE = "CMS_UPLOAD_IMAGE",
  UPLOAD_IMAGE_SUCCESS = "CMS_UPLOAD_IMAGE_SUCCESS",
  UPLOAD_IMAGE_FAILURE = "CMS_UPLOAD_IMAGE_FAILURE",
  SET_UPLOADED_IMAGE = "SET_UPLOADED_IMAGE",
  CLEAR_UPLOADED_IMAGE = "CLEAR_UPLOADED_IMAGE",

  //channels
  GET_CHANNELS = "GET_CHANNELS",
  SET_CHANNELS = "SET_CHANNELS",
  GET_CHANNEL = "GET_CHANNEL",
  SET_CHANNEL = "SET_CHANNEL",
  CLEAR_CHANNEL = "CLEAR_CHANNEL",
  CREATE_CHANNEL = "CREATE_CHANNEL",
  UPDATE_CHANNEL = "UPDATE_CHANNEL",
  DELETE_CHANNEL = "DELETE_CHANNEL",

  // EPG
  GET_EPG = "GET_EPG",
  SET_EPG = "SET_EPG",
  CLEAR_EPG = "CLEAR_EPG",

  //tags
  GET_TAGS = "GET_CMS_TAGS",
  SET_TAGS = "SET_CMS_TAGS",
  GET_TAG = "GET_CMS_TAG",
  SET_TAG = "SET_CMS_TAG",
  CLEAR_TAG = "CLEAR_CMS_TAG",
  CREATE_TAG = "CREATE_CMS_TAG",
  UPDATE_TAG = "UPDATE_CMS_TAG",
  DELETE_TAG = "DELETE_CMS_TAG",

  //genres
  GET_GENRES = "GET_CMS_GENRES",
  SET_GENRES = "SET_CMS_GENRES",

  //collections
  GET_COLLECTIONS = "GET_CMS_COLLECTIONS",
  SET_COLLECTIONS = "SET_CMS_COLLECTIONS",
  GET_COLLECTION = "GET_CMS_COLLECTION",
  SET_COLLECTION = "SET_CMS_COLLECTION",
  CLEAR_COLLECTION = "CLEAR_CMS_COLLECTION",
  CREATE_COLLECTION = "CREATE_CMS_COLLECTION",
  UPDATE_COLLECTION = "UPDATE_CMS_COLLECTION",
  DELETE_COLLECTION = "DELETE_CMS_COLLECTION",

  //entitlements (asset ↔ collection membership)
  UPDATE_ASSET_ENTITLEMENTS = "UPDATE_ASSET_ENTITLEMENTS",

  //loader
  CLEAR_LOADING = "CLEAR_CMS_LOADING",
}

export interface CMSAction {
  type: CMS_ACTION;
  payload?: any;
}

// -- Action Creators --
//assets
export function GetAssetsAction(query: string): CMSAction {
  return {
    type: CMS_ACTION.GET_ASSETS,
    payload: { query },
  };
}

export function SetAssetsAction(
  assets: CMSAsset[],
  pagination: CMSPagination
): CMSAction {
  return {
    type: CMS_ACTION.SET_ASSETS,
    payload: { assets, pagination },
  };
}

export function SetProvidersAction(
  providers: (Provider | CmsTag)[]
): CMSAction {
  return {
    type: CMS_ACTION.SET_PROVIDERS,
    payload: { providers },
  };
}

export function GetAssetAction(id: string): CMSAction {
  return {
    type: CMS_ACTION.GET_ASSET,
    payload: { id },
  };
}

export function SetAssetAction(asset?: CMSAsset): CMSAction {
  return {
    type: CMS_ACTION.SET_ASSET,
    payload: { asset },
  };
}

export function ClearAssetAction(): CMSAction {
  return {
    type: CMS_ACTION.CLEAR_ASSET,
  };
}

export function CreateAssetAction(assetData: {
  name: string;
  type: string;
}): CMSAction {
  return {
    type: CMS_ACTION.CREATE_ASSET,
    payload: assetData,
  };
}

export function UpdateAssetAction(asset: CMSAsset): CMSAction {
  return {
    type: CMS_ACTION.UPDATE_ASSET,
    payload: { asset },
  };
}

export function DeleteAssetAction(id: string): CMSAction {
  return {
    type: CMS_ACTION.DELETE_ASSET,
    payload: { id },
  };
}

//providers
export function GetProvidersAction(): CMSAction {
  return {
    type: CMS_ACTION.GET_PROVIDERS,
    payload: {},
  };
}

//medias
export function CreateMediaAction(assetId: string, media: CMSMedia): CMSAction {
  return {
    type: CMS_ACTION.CREATE_MEDIA,
    payload: { assetId, media },
  };
}

export function UpdateMediaAction(assetId: string, media: CMSMedia): CMSAction {
  return {
    type: CMS_ACTION.UPDATE_MEDIA,
    payload: { assetId, media },
  };
}

export function DeleteMediaAction(assetId: string, mediaId: string): CMSAction {
  return {
    type: CMS_ACTION.DELETE_MEDIA,
    payload: { assetId, mediaId },
  };
}

export function UploadImageAction(
  assetId: string,
  image: File,
  index: number
): CMSAction {
  return {
    type: CMS_ACTION.UPLOAD_IMAGE,
    payload: { assetId, image, index },
  };
}

function UploadImageSuccess(imageUrl: string, imageIndex: number): CMSAction {
  return {
    type: CMS_ACTION.UPLOAD_IMAGE_SUCCESS,
    payload: { imageUrl, imageIndex },
  };
}

function UploadImageFailure(error: any): CMSAction {
  return {
    type: CMS_ACTION.UPLOAD_IMAGE_FAILURE,
    payload: error,
  };
}

export function SetUploadedImageAction(
  url: string,
  blobPath: string,
  index: number
): CMSAction {
  return {
    type: CMS_ACTION.SET_UPLOADED_IMAGE,
    payload: { url, blobPath, index },
  };
}

export function ClearUploadedImageAction(): CMSAction {
  return {
    type: CMS_ACTION.CLEAR_UPLOADED_IMAGE,
  };
}

//channels
export function GetChannelsAction(query: string): CMSAction {
  return {
    type: CMS_ACTION.GET_CHANNELS,
    payload: { query },
  };
}

export function SetChannelsAction(
  channels: CMSAsset[],
  pagination?: CMSPagination
): CMSAction {
  return {
    type: CMS_ACTION.SET_CHANNELS,
    payload: { channels, pagination },
  };
}

export function GetChannelAction(id: string): CMSAction {
  return {
    type: CMS_ACTION.GET_CHANNEL,
    payload: { id },
  };
}

export function SetChannelAction(channel?: CMSAsset): CMSAction {
  return {
    type: CMS_ACTION.SET_CHANNEL,
    payload: { channel },
  };
}

export function ClearChannelAction(): CMSAction {
  return {
    type: CMS_ACTION.CLEAR_CHANNEL,
  };
}

export function CreateChannelAction(name: string): CMSAction {
  return {
    type: CMS_ACTION.CREATE_CHANNEL,
    payload: { name },
  };
}

export function UpdateChannelAction(channel: CMSAsset): CMSAction {
  return {
    type: CMS_ACTION.UPDATE_CHANNEL,
    payload: { channel },
  };
}

export function DeleteChannelAction(id: string): CMSAction {
  return {
    type: CMS_ACTION.DELETE_CHANNEL,
    payload: { id },
  };
}

//EPG
export function GetEPGAction(id: string): CMSAction {
  return { type: CMS_ACTION.GET_EPG, payload: { id } };
}

export function SetEPGAction(EPG?: CMSEPGEvent[]): CMSAction {
  return { type: CMS_ACTION.SET_EPG, payload: { EPG } };
}

export function ClearEPGAction(): CMSAction {
  return { type: CMS_ACTION.CLEAR_EPG };
}

//tags
export function GetTagsAction(searchQuery?: string): CMSAction {
  return { type: CMS_ACTION.GET_TAGS, payload: { searchQuery } };
}

export function SetTagsAction(
  tags: ReturnType<CMSTag["serialize"]>[],
  pagination: CMSPagination
): CMSAction {
  return { type: CMS_ACTION.SET_TAGS, payload: { tags, pagination } };
}

export function GetTagAction(id: string): CMSAction {
  return { type: CMS_ACTION.GET_TAG, payload: { id } };
}

export function SetTagAction(tag?: ReturnType<CMSTag["serialize"]>): CMSAction {
  return { type: CMS_ACTION.SET_TAG, payload: { tag } };
}

export function ClearTagAction(): CMSAction {
  return { type: CMS_ACTION.CLEAR_TAG };
}

export function CreateTagAction(tagData: {
  name: string;
  type: string;
}): CMSAction {
  return { type: CMS_ACTION.CREATE_TAG, payload: tagData };
}

export function UpdateTagAction(
  tag: ReturnType<CMSTag["serialize"]>
): CMSAction {
  return { type: CMS_ACTION.UPDATE_TAG, payload: { tag } };
}

export function DeleteTagAction(id: string): CMSAction {
  return { type: CMS_ACTION.DELETE_TAG, payload: { id } };
}

//genres
export function GetGenresAction(): CMSAction {
  return { type: CMS_ACTION.GET_GENRES };
}

export function SetGenresAction(genres: CMSGenre[]): CMSAction {
  return { type: CMS_ACTION.SET_GENRES, payload: { genres } };
}
//collection
export function GetCollectionsAction(searchQuery?: string): CMSAction {
  return { type: CMS_ACTION.GET_COLLECTIONS, payload: { searchQuery } };
}

// Accept plain serialized collection objects
export function SetCollectionsAction(
  collections: ReturnType<CMSCollection["serialize"]>[]
): CMSAction {
  return { type: CMS_ACTION.SET_COLLECTIONS, payload: { collections } };
}

export function GetCollectionAction(id: string): CMSAction {
  return { type: CMS_ACTION.GET_COLLECTION, payload: { id } };
}

// Accept plain serialized collection object (optional)
export function SetCollectionAction(
  collection?: ReturnType<CMSCollection["serialize"]>
): CMSAction {
  return { type: CMS_ACTION.SET_COLLECTION, payload: { collection } };
}

export function ClearCollectionAction(): CMSAction {
  return { type: CMS_ACTION.CLEAR_COLLECTION };
}

export function CreateCollectionAction(name: string): CMSAction {
  return { type: CMS_ACTION.CREATE_COLLECTION, payload: { name } };
}

// Already updated correctly
export function UpdateCollectionAction(
  collection: ReturnType<CMSCollection["serialize"]>
): CMSAction {
  return { type: CMS_ACTION.UPDATE_COLLECTION, payload: { collection } };
}

export function DeleteCollectionAction(id: string): CMSAction {
  return { type: CMS_ACTION.DELETE_COLLECTION, payload: { id } };
}

export function UpdateAssetEntitlementsAction(
  assetId: string,
  collectionIds: string[]
): CMSAction {
  return {
    type: CMS_ACTION.UPDATE_ASSET_ENTITLEMENTS,
    payload: { assetId, collectionIds },
  };
}

export function ClearCMSLoadingAction(): CMSAction {
  return { type: CMS_ACTION.CLEAR_LOADING };
}

// -- selectors --
export const getIsLoading = (state: CustomerPortalState): boolean => {
  return state.CMS.isLoading;
};

export const getUploadedImagesSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.uploadedImages],
  (localizedImagesForUpload) => localizedImagesForUpload
);

export const getVODsSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.assets],
  (assets = []) =>
    assets.map((asset) => {
      return fromJson(CMSAsset, asset) as CMSAsset;
    })
);

export const getProvidersSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.providers],
  (providers) => providers
);

export const getPaginatorSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.pagination],
  (pagination) => pagination as CMSPagination
);

export const getVODSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.asset],
  (asset) => asset && (fromJson(CMSAsset, asset) as CMSAsset)
);

export const getTagsSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.tags],
  (tags = []) =>
    tags.map((tag) => {
      return fromJson(CMSTag, tag) as CMSTag;
    })
);

export const getTagSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.tag],
  (tag) => tag && (fromJson(CMSTag, tag) as CMSTag)
);

export const getMediaCollectionsSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.collections],
  (collections = []) =>
    collections.map((collection) => {
      return fromJson(CMSCollection, collection) as CMSCollection;
    })
);

const getIdsArg = (_: CustomerPortalState, ids: string[] = []) => ids;

export const selectMediaCollectionsByIds = createSelector(
  [getMediaCollectionsSelector, getIdsArg],
  (collections, ids) => {
    if (!ids.length) return [];
    const idSet = new Set(ids);
    return collections.filter((c) => idSet.has(c._id));
  }
);

export const getMediaCollectionSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.collection],
  (collection) =>
    collection && (fromJson(CMSCollection, collection) as CMSCollection)
);

export const getLiveChannelsSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.channels],
  (channels = []) =>
    channels.map((channel) => {
      return fromJson(CMSAsset, channel) as CMSAsset;
    })
);

export const getLiveChannelSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.channel],
  (channel) => channel && (fromJson(CMSAsset, channel) as CMSAsset)
);

export const getGenresSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.genres],
  (genres = []) =>
    genres.map((genre) => {
      return fromJson(CMSGenre, genre) as CMSGenre;
    })
);

const EMPTY_EPG = [];

export const getEPGSelector = createSelector(
  [(state: CustomerPortalState) => state.CMS.EPG],
  (EPG = []) => {
    if (EPG.length === 0) return EMPTY_EPG;
    return EPG.map((event) => {
      return fromJson(CMSEPGEvent, event) as CMSEPGEvent;
    });
  }
);

// -- Reducer --
export default function cmsReducer(
  state = initialState,
  action: CMSAction
): CMSState {
  switch (action.type) {
    case CMS_ACTION.CREATE_ASSET:
    case CMS_ACTION.GET_ASSETS:
    case CMS_ACTION.GET_ASSET:
    case CMS_ACTION.UPDATE_ASSET:
    case CMS_ACTION.DELETE_ASSET:
    case CMS_ACTION.CREATE_MEDIA:
    case CMS_ACTION.UPDATE_MEDIA:
    case CMS_ACTION.DELETE_MEDIA:
    case CMS_ACTION.UPLOAD_IMAGE:
    case CMS_ACTION.GET_PROVIDERS:
    case CMS_ACTION.GET_CHANNELS:
    case CMS_ACTION.GET_CHANNEL:
    case CMS_ACTION.CREATE_CHANNEL:
    case CMS_ACTION.DELETE_CHANNEL:
    case CMS_ACTION.UPDATE_CHANNEL:
    case CMS_ACTION.CREATE_TAG:
    case CMS_ACTION.GET_TAGS:
    case CMS_ACTION.GET_TAG:
    case CMS_ACTION.UPDATE_TAG:
    case CMS_ACTION.DELETE_TAG:
    case CMS_ACTION.GET_EPG:
    case CMS_ACTION.GET_COLLECTIONS:
    case CMS_ACTION.GET_COLLECTION:
    case CMS_ACTION.UPDATE_COLLECTION:
    case CMS_ACTION.DELETE_COLLECTION:
    case CMS_ACTION.CREATE_COLLECTION:
    case CMS_ACTION.GET_GENRES:
      return {
        ...state,
        isLoading: true,
      };
    case CMS_ACTION.SET_ASSETS:
      return {
        ...state,
        assets: action.payload.assets,
        pagination: action.payload.pagination,
        isLoading: false,
      };
    case CMS_ACTION.SET_ASSET:
      return {
        ...state,
        asset: action.payload.asset,
        isLoading: false,
      };
    case CMS_ACTION.CLEAR_ASSET:
      return {
        ...state,
        asset: undefined,
      };
    case CMS_ACTION.SET_PROVIDERS:
      return {
        ...state,
        providers: action.payload.providers,
        isLoading: false,
      };
    case CMS_ACTION.SET_CHANNELS:
      return {
        ...state,
        channels: action.payload.channels,
        pagination: action.payload.pagination ?? state.pagination,
        isLoading: false,
      };
    case CMS_ACTION.SET_CHANNEL:
      return {
        ...state,
        channel: action.payload.channel,
        isLoading: false,
      };
    case CMS_ACTION.CLEAR_CHANNEL:
      return {
        ...state,
        channel: undefined,
      };
    case CMS_ACTION.SET_EPG:
      return {
        ...state,
        EPG: action.payload.EPG,
        isLoading: false,
      };
    case CMS_ACTION.CLEAR_EPG:
      return {
        ...state,
        EPG: undefined,
      };
    case CMS_ACTION.SET_TAGS:
      return {
        ...state,
        tags: action.payload.tags,
        pagination: action.payload.pagination,
        isLoading: false,
      };
    case CMS_ACTION.SET_TAG:
      return {
        ...state,
        tag: action.payload.tag,
        isLoading: false,
      };
    case CMS_ACTION.CLEAR_TAG:
      return {
        ...state,
        tag: undefined,
      };
    case CMS_ACTION.SET_GENRES:
      return {
        ...state,
        genres: action.payload.genres,
        isLoading: false,
      };
    case CMS_ACTION.SET_COLLECTIONS:
      return {
        ...state,
        collections: action.payload.collections,
        isLoading: false,
      };
    case CMS_ACTION.SET_COLLECTION:
      return {
        ...state,
        collection: action.payload.collection,
        isLoading: false,
      };
    case CMS_ACTION.CLEAR_COLLECTION:
      return {
        ...state,
        collection: undefined,
      };
    case CMS_ACTION.SET_UPLOADED_IMAGE:
      const { url, blobPath, index } = action.payload;
      return {
        ...state,
        uploadedImages: [...state.uploadedImages, { url, blobPath, index }],
      };
    case CMS_ACTION.CLEAR_UPLOADED_IMAGE:
      return { ...state, uploadedImages: [] };
    case CMS_ACTION.UPLOAD_IMAGE_SUCCESS:
      return { ...state, isLoading: false };
    case CMS_ACTION.UPLOAD_IMAGE_FAILURE:
      return {
        ...state,
        isLoading: false,
      };
    case CMS_ACTION.CLEAR_LOADING:
      return {
        ...state,
        isLoading: false,
      };
    default:
      return state;
  }
}

// -- Epics --

const customErrorHandler = (err) =>
  from([
    ClearCMSLoadingAction(),
    SetError(err.response ? err.response.message : err.message),
    SetNotificationError(t("common.something_went_wrong")),
  ]);

//assets
const getAssets$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_ASSETS),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getAssets(a.payload.query).pipe(
        map(({ pagination, assets }) => {
          return SetAssetsAction(assets, pagination);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const getAsset$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_ASSET),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getAsset(a.payload.id).pipe(
        map((asset) => {
          return SetAssetAction(asset);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const createAsset$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.CREATE_ASSET),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.createAsset(a.payload).pipe(
        map((asset) => {
          return SetAssetAction(asset);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const updateAsset$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.UPDATE_ASSET),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.updateAsset(a.payload.asset).pipe(
        mergeMap((asset) => {
          return from([
            SetAssetAction(asset),
            SetNotificationSuccess(t("common.save_success")),
          ]);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const deleteAsset$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.DELETE_ASSET),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.deleteAsset(a.payload.id).pipe(
        map(() => {
          return SetAssetAction(undefined);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

//providers
const getProviders$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_PROVIDERS),
    debounceTime(200),
    switchMap(() => {
      return cmsService.getProviders().pipe(
        map((providers) => {
          return SetProvidersAction(providers);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

//medias
const createMedia$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.CREATE_MEDIA),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.createMedia(a.payload.assetId, a.payload.media).pipe(
        map((media) => {
          return GetAssetAction(a.payload.assetId);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const updateMedia$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.UPDATE_MEDIA),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.updateMedia(a.payload.assetId, a.payload.media).pipe(
        mergeMap(() => {
          return from([
            GetAssetAction(a.payload.assetId),
            SetNotificationError(t("common.something_went_wrong")),
          ]);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const deleteMedia$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.DELETE_MEDIA),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.deleteMedia(a.payload.assetId, a.payload.mediaId).pipe(
        map(() => {
          return GetAssetAction(a.payload.assetId);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const uploadFile$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.UPLOAD_IMAGE),
    switchMap((a) => {
      return cmsService
        .uploadFile(a.payload.assetId, a.payload.image, "asset-images")
        .pipe(
          switchMap((response: { fileUrl: string; blobPath: string }) => {
            return from([
              UploadImageSuccess(response.fileUrl, a.payload.index),
              SetUploadedImageAction(
                response.fileUrl,
                response.blobPath,
                a.payload.index
              ),
              SetNotificationSuccess("Image uploaded"),
            ]);
          }),
          catchError((error) =>
            from([UploadImageFailure(error), SetNotificationError(error)])
          )
        );
    })
  );
};

//channels
const getChannels$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_CHANNELS),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getChannels(a.payload.query).pipe(
        map((response) => {
          return SetChannelsAction(response.assets, response.pagination);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const getChannel$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_CHANNEL),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getChannel(a.payload.id).pipe(
        map((channel) => {
          return SetChannelAction(channel);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const createChannel$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.CREATE_CHANNEL),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.createChannel(a.payload.name).pipe(
        map((channel) => {
          return SetChannelAction(channel);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const updateChannel$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.UPDATE_CHANNEL),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.updateChannel(a.payload.channel).pipe(
        mergeMap((channel) => {
          return from([
            SetChannelAction(channel),
            SetNotificationSuccess(t("common.save_success")),
          ]);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const deleteChannel$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.DELETE_CHANNEL),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.deleteChannel(a.payload.id).pipe(
        map(() => {
          return SetChannelAction(undefined);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

//Epg
const getEPG$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_EPG),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getEPG(a.payload.id).pipe(
        map((EPG) => {
          return SetEPGAction(EPG);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

//tags
const getTags$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_TAGS),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getTags(a.payload.searchQuery).pipe(
        map((data) => {
          return SetTagsAction(data.tags, data.pagination);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const getTag$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_TAG),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getTag(a.payload.id).pipe(
        map((tag) => {
          return SetTagAction(tag);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const createTag$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.CREATE_TAG),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.createTag(a.payload).pipe(
        map((tag) => {
          return SetTagAction(tag);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const updateTag$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.UPDATE_TAG),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.updateTag(a.payload.tag).pipe(
        mergeMap((tag) => {
          return from([
            SetTagAction(tag),
            SetNotificationSuccess(t("common.save_success")),
          ]);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const deleteTag$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.DELETE_TAG),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.deleteTag(a.payload.id).pipe(
        map(() => {
          return SetTagAction(undefined);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

//genres
const getGenres$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_GENRES),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getGenres().pipe(
        map((genres) => {
          return SetGenresAction(genres);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

//collections
const getCollections$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_COLLECTIONS),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getCollections(a.payload.searchQuery).pipe(
        map((collections) => {
          return SetCollectionsAction(collections);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const getCollection$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.GET_COLLECTION),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.getCollection(a.payload.id).pipe(
        map((collection) => {
          return SetCollectionAction(collection);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const createCollection$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.CREATE_COLLECTION),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.createCollection(a.payload.name).pipe(
        map((collection) => {
          return SetCollectionAction(collection);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const updateCollection$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.UPDATE_COLLECTION),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.updateCollection(a.payload.collection).pipe(
        mergeMap((collection) => {
          return from([
            SetCollectionAction(collection),
            SetNotificationSuccess(t("common.save_success")),
          ]);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const deleteCollection$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.DELETE_COLLECTION),
    debounceTime(200),
    switchMap((a) => {
      return cmsService.deleteCollection(a.payload.id).pipe(
        map(() => {
          return SetCollectionAction(undefined);
        }),
        catchError(customErrorHandler)
      );
    })
  );
};

const updateAssetEntitlements$: Epic = (action$) => {
  return action$.pipe(
    ofType(CMS_ACTION.UPDATE_ASSET_ENTITLEMENTS),
    switchMap((a) => {
      return cmsService
        .updateAssetCollections(a.payload.assetId, a.payload.collectionIds)
        .pipe(
          map(() => ClearCMSLoadingAction()),
          catchError(customErrorHandler)
        );
    })
  );
};

export const cmsEpics = [
  getAssets$,
  getAsset$,
  createAsset$,
  updateAsset$,
  deleteAsset$,
  getProviders$,
  createMedia$,
  updateMedia$,
  deleteMedia$,
  uploadFile$,
  getChannels$,
  getChannel$,
  createChannel$,
  updateChannel$,
  deleteChannel$,
  getEPG$,
  getTags$,
  getTag$,
  createTag$,
  updateTag$,
  deleteTag$,
  getGenres$,
  getCollections$,
  getCollection$,
  createCollection$,
  updateCollection$,
  deleteCollection$,
  updateAssetEntitlements$,
];
